import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { toast } from 'react-toastify';

import { Button } from '@material-ui/core';
import { Add, Remove, Refresh } from '@material-ui/icons';
import { isEmpty, differenceBy, findIndex } from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Table, Modal } from '~/components';
import { t } from '~/helpers';

import { MTable } from './components';
import * as S from './styles';

export default function SelectPostoBicoDataInTable({
  date,
  time,
  height,
  // cells,
  size,
  onChanged,
  title,
  showCount,
  paginate,
  unique,
  items,
  label,
  disablePreviousDate,
  disableFutureDate,
  initials,
  // fields,
  onField,
  schema,
  isIgnore,
  position,
  maxWidth,
  titleModal,
  checkbox,
  radio,
  create,
  remove,
  icons,
  variant,
  showPagination,
  onCallback,
  noCreate,
  noRemove,
  noUpdate,
  onRow,
  loading,
  sendWhenEditing,
  ignoreExtra,
}) {
  const [data, setData] = useState(initials);
  const [head, setHead] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [isUpdated, setIsUpdated] = useState(false);

  const tableRef = useRef(null);

  const schemapostobicos = useMemo(
    () => ({
      idposto: Yup.string().required(t('s.geral.validacao.campoobrigatorio')),
      // idbico: Yup.number()
      //   .required(t('s.geral.validacao.campoobrigatorio')),
      // idbico: Yup.number()
      //   .required(t('s.geral.validacao.campoobrigatorio'))
      //   .test(
      //     'bico-ja-existe',
      //     t('s.geral.validacao.bicojaexiste'),
      //     function (value) {
      //       return !data.some(bico => bico.idbico === value);
      //     }
      //   ),
      idbico: Yup.number()
        .required(t('s.geral.validacao.campoobrigatorio'))
        .test(
          'bico-nome-ja-existe',
          t('s.geral.validacao.bicoenomejaexistem'), // Ex: "Essa combinação de bico e nome já está cadastrada"
          function (value) {
            const { idposto } = this.parent;
            return !data.some(
              (bico) =>
                bico.idbico === value && bico.idposto === Number(idposto)
            );
          }
        ),
      vazaomaxima: Yup.number()
        .required(t('s.geral.validacao.campoobrigatorio'))
        .transform((value) => (isNaN(value) ? undefined : value))
        // .integer(t('s.geral.validacao.deveserinteiro'))
        .moreThan(-1, t('s.geral.validacao.valorminimo').replace('%s', 0))
        .lessThan(2001, t('s.geral.validacao.valormaximo').replace('%s', 2000))
        .typeError(t('s.geral.validacao.campoobrigatorio')),
    }),
    [data]
  );

  const cellspostobicos = useMemo(
    () => [
      { id: 'codigonomeposto', label: t('s.menu.configuracao.posto') },
      { id: 'idbico', label: t('s.configuracao.bico.idbico') },
      {
        id: 'vazaomaxima',
        label: t('s.operacao.abastecimento.vazaomaximamovimentacao'),
      },
    ],
    []
  );

  const fieldspostobicos = useMemo(
    () => [
      {
        id: 'idposto',
        type: 'autocomplete',
        label: t('s.menu.configuracao.posto'),
        option: {
          label: 'codigonomeposto',
          url: 'postoscomboios',
          unique: 'idposto',
          hiddens: [{ name: 'codigonomeposto' }],
        },
      },
      {
        id: 'idbico',
        label: t('s.configuracao.bico.idbico'),
        type: 'number',
      },
      {
        id: 'vazaomaxima',
        label: t('s.operacao.abastecimento.vazaomaximamovimentacao'),
        type: 'number',
      },
    ],
    []
  );

  useEffect(() => {
    setData(initials);
  }, [initials]);

  const handleOpenModal = useCallback(
    (params) => {
      if (isEmpty(fieldspostobicos)) {
        if (date) setIsOpen(true);
        else tableRef.current.handleOpenModal();
      } else {
        tableRef.current.handleOpenModal({ initials: params });
      }
    },
    [date, fieldspostobicos]
  );

  const handleAddDateTime = useCallback((isDate) => {
    setData((state) => [...state, { date: isDate }]);
  }, []);

  const handleAddSelectItem = useCallback((value) => {
    setData((state) => {
      const testArray = state.some((obj) =>
        unique.every((campo) => obj[campo] === value[campo])
      );
      if (!testArray) {
        return [
          ...state,
          {
            ...value,
          },
        ];
      }
      toast.error('Erro, parâmetro já existe na tabela !');
      return state;
    });
    tableRef.current.handleCloseModal();
  }, []);

  const handleAddItem = useCallback((value) => {
    setData((state) => [...state, value]);
    setIsUpdated(true);
    tableRef.current.handleCloseModal();
  }, []);

  const handleModifyItem = useCallback(
    (value) => {
      const key = findIndex(data, (d) => d[unique] === value[unique]);

      setIsUpdated(true);
      setData((state) =>
        state.map((rest, index) => {
          if (index === key) return value;
          return rest;
        })
      );

      tableRef.current.handleCloseModal();
      setSelected([]);
    },
    [data, unique]
  );

  const handleSelectItems = useCallback((values) => {
    setSelected(values);
  }, []);

  const handleRemoveItems = useCallback(() => {
    setIsUpdated(true);
    onCallback({ type: 'remove', data: selected });
    setData((state) => differenceBy(state, selected, unique));
    setSelected([]);
  }, [selected, unique]);

  useEffect(() => {
    if (!isEmpty(cellspostobicos) && !date) setHead(cellspostobicos);
    if (date) setHead([{ id: 'date', label: 'Data' }]);
  }, [cellspostobicos, date]);

  useEffect(() => {
    if (sendWhenEditing) {
      if (isUpdated) {
        onChanged(data);
      }
    } else {
      onChanged(data);
    }
  }, [data, sendWhenEditing, isUpdated]);

  return (
    <>
      <S.Wrapper position={position}>
        <S.GroupButtons position={position}>
          {create && !noCreate && (
            <Button
              color="primary"
              variant={variant}
              size="small"
              startIcon={icons ? <Add /> : undefined}
              onClick={() => handleOpenModal({})}
              disabled={loading}
            >
              {t('s.geral.botaoincluir')}
            </Button>
          )}

          {!isEmpty(fieldspostobicos) && !noUpdate && (
            <Button
              color="primary"
              variant={variant}
              size="small"
              startIcon={icons ? <Refresh /> : undefined}
              disabled={selected.length !== 1 || loading}
              onClick={() => handleOpenModal(selected[0])}
            >
              {t('s.geral.botaoalterar')}
            </Button>
          )}

          {remove && !noRemove && (
            <Button
              color="secondary"
              variant={variant}
              size="small"
              startIcon={icons ? <Remove /> : undefined}
              disabled={isEmpty(selected) || loading}
              onClick={handleRemoveItems}
            >
              {t('s.geral.botaoexcluir')}
            </Button>
          )}
        </S.GroupButtons>

        <Table
          showPagination={showPagination}
          checkbox={checkbox && !radio}
          radio={radio}
          noButton
          cells={head}
          data={data}
          clearCheckeds={isEmpty(selected)}
          height={`${height}rem`}
          size={size}
          title={title}
          showCount={showCount}
          paginate={paginate}
          unique={unique}
          onCallback={handleSelectItems}
          onRow={onRow}
        />
      </S.Wrapper>

      <Modal
        ref={tableRef}
        form
        disableBackdropClick
        disableEscapeKeyDown
        maxWidth={maxWidth}
        title={titleModal}
        component={(props) => (
          <MTable
            {...props}
            items={items}
            ignore={data.concat(ignoreExtra)}
            onChangeItem={handleAddSelectItem}
            // onChangeItem={handleAddItem}
            onModify={handleModifyItem}
            label={label}
            fields={fieldspostobicos}
            onField={onField}
            isSchema={schemapostobicos}
            isIgnore={isIgnore}
            onCallback={onCallback}
          />
        )}
      />
    </>
  );
}

SelectPostoBicoDataInTable.propTypes = {
  date: PropTypes.bool,
  time: PropTypes.bool,
  height: PropTypes.number,
  // cells: PropTypes.arrayOf(
  //   PropTypes.shape({
  //     id: PropTypes.string.isRequired,
  //     label: PropTypes.string.isRequired,
  //     format: PropTypes.func,
  //   })
  // ).isRequired,
  size: PropTypes.oneOf(['small', 'medium']),
  onChanged: PropTypes.func.isRequired,
  title: PropTypes.string,
  showCount: PropTypes.bool,
  paginate: PropTypes.number,
  unique: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType(PropTypes.string, PropTypes.number).isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType(PropTypes.string, PropTypes.number).isRequired,
    })
  ),
  label: PropTypes.string,
  disablePreviousDate: PropTypes.bool,
  disableFutureDate: PropTypes.bool,
  ignoreExtra: PropTypes.arrayOf(PropTypes.any),
  initials: PropTypes.arrayOf(PropTypes.any),
  // fields: PropTypes.arrayOf(
  //   PropTypes.shape({
  //     id: PropTypes.string.isRequired,
  //     label: PropTypes.string.isRequired,
  //     type: PropTypes.string.isRequired,
  //     options: PropTypes.arrayOf(),
  //     disabled: PropTypes.bool,
  //   })
  // ),
  onField: PropTypes.func,
  schema: PropTypes.objectOf(PropTypes.any),
  isIgnore: PropTypes.string,
  position: PropTypes.oneOf(['row', 'column']),
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  titleModal: PropTypes.string,
  checkbox: PropTypes.bool,
  radio: PropTypes.bool,
  create: PropTypes.bool,
  remove: PropTypes.bool,
  icons: PropTypes.bool,
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  showPagination: PropTypes.bool,
  onCallback: PropTypes.func,
  noCreate: PropTypes.bool,
  noRemove: PropTypes.bool,
  noUpdate: PropTypes.bool,
  onRow: PropTypes.func,
  loading: PropTypes.bool,
  sendWhenEditing: PropTypes.bool,
};

SelectPostoBicoDataInTable.defaultProps = {
  date: false,
  time: false,
  height: 20,
  size: 'medium',
  title: '',
  showCount: false,
  paginate: 5,
  unique: 'date',
  items: [],
  label: '',
  disablePreviousDate: false,
  disableFutureDate: false,
  initials: [],
  ignoreExtra: [],
  // fields: {},
  onField: (value) => value,
  schema: {},
  isIgnore: '',
  position: 'column',
  maxWidth: 'md',
  titleModal: '',
  checkbox: true,
  radio: false,
  create: true,
  remove: true,
  icons: true,
  variant: 'outlined',
  showPagination: true,
  onCallback: (value) => value,
  noCreate: false,
  noRemove: false,
  noUpdate: false,
  onRow: () => undefined,
  loading: false,
  sendWhenEditing: false,
};
